import { SquarePen } from 'lucide-react';

import Composer from './components/viewHelpAssistantSheet/Composer';
import Failure from './components/viewHelpAssistantSheet/Failure';
import Message from './components/viewHelpAssistantSheet/Message';
import Question from './components/viewHelpAssistantSheet/Question';
import Thinking from './components/viewHelpAssistantSheet/Thinking';
import Welcome from './components/viewHelpAssistantSheet/Welcome';

import { useHelpAssistantContext } from '@/app/helpArticle/_context';

import { Button } from '@/app/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/app/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/ui/tooltip';

// Keeps the newest turn in view as the conversation grows.
const scrollIntoView = (node) => {
  node?.scrollIntoView({ block: 'end' });
};

/**
 * The help centre assistant: a conversation answered from the help articles.
 * The conversation is the routes' state, not this panel's, so closing the panel
 * or following a source link keeps it. Nothing is stored anywhere.
 */
const ViewHelpAssistantSheet = ({ onClose }) => {
  const { conversation } = useHelpAssistantContext();
  const { messages, pending, ask, retry, reset } = conversation;
  const isThinking = Boolean(pending?.isThinking);
  const isEmpty = messages.length === 0 && !pending;

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="w-full gap-0 sm:w-[30rem] sm:max-w-none" data-testid="help-assistant-sheet">
        {/* The title row is always h-8, with or without New Chat, and pt-1 centres it on the sheet's own close button. */}
        <SheetHeader className="gap-0 border-b px-4 pt-1 pb-3">
          <div className="flex h-8 items-center justify-between gap-2 pr-7">
            <SheetTitle className="leading-6">Ask Tito</SheetTitle>
            {!isEmpty && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="text-muted-foreground" onClick={reset} disabled={isThinking} data-testid="help-assistant-reset-button">
                    <SquarePen />
                    <span className="sr-only">New Chat</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>New Chat</TooltipContent>
              </Tooltip>
            )}
          </div>
          <SheetDescription>Answers from the Zebel help articles.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 py-5" data-testid="help-assistant-conversation">
          {isEmpty ? (
            <Welcome onAsk={ask} />
          ) : (
            <div className="space-y-6">
              {messages.map((message, index) => (
                <Message key={index} message={message} />
              ))}
              {pending && <Question content={pending.question} />}
              {isThinking && <Thinking />}
              {pending?.error && <Failure error={pending.error} onRetry={retry} />}
              <div ref={scrollIntoView} key={messages.length + (pending ? 1 : 0)} />
            </div>
          )}
        </div>
        <div className="bg-muted/30 border-t px-4 pt-3 pb-2.5">
          <Composer isThinking={isThinking} onAsk={ask} />
          <p className="text-muted-foreground mt-2 text-center text-xs" data-testid="help-assistant-disclaimer">
            AI can make mistakes. Check the linked articles.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ViewHelpAssistantSheet;
